// File: src/components/ReadingRoom/PDFReader.jsx
// Description: Enhanced PDF reader modal with improved controls and features

import React, { useEffect, useState, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { 
  XMarkIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  PrinterIcon,
  ShareIcon,
  BookmarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const PDFReader = ({ resource, isOpen, onClose }) => {
  const { currentLanguage, getText } = useLanguage();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);
  const modalRef = useRef(null);

  // Handle escape key and outside clicks
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !isFullscreen) {
        onClose();
      }
    };

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target) && !isFullscreen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, isFullscreen]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setError(null);
      setZoomLevel(100);
      setCurrentPage(1);
    }
  }, [isOpen, resource]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    // In a real implementation, you would get the total pages from the PDF
    setTotalPages(10); // Mock value
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setError(getText({
      en: 'Failed to load PDF. Please try downloading the file instead.',
      mm: 'PDF ဖိုင် ဖွင့်၍မရပါ။ ဖိုင်ကို ဒေါင်းလုဒ်လုပ်ကြည့်ပါ။'
    }));
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = resource.downloadUrl;
    link.download = `${getText(resource.title)}.${resource.format.toLowerCase()}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.print();
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: getText(resource.title),
          text: getText(resource.description),
          url: resource.readUrl
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(resource.readUrl);
    }
  };

  const handleBookmark = () => {
    // In real app, this would save bookmark with current page
    console.log('Bookmark added for page:', currentPage);
  };

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      try {
        await modalRef.current.requestFullscreen();
      } catch (error) {
        console.error('Fullscreen failed:', error);
      }
    } else {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.error('Exit fullscreen failed:', error);
      }
    }
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 25, 50));
  };

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    } else if (direction === 'prev' && currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  if (!isOpen || !resource) return null;

  // 🚨 CRITICAL GUARD: Never render iframe if URL is null
  if (!resource.readUrl) {
    return (
      <div className="pdf-reader-modal">
        <div className="reader-overlay" onClick={onClose}></div>
        <div className="reader-container" ref={modalRef}>
          <div className="pdf-modal">
            <div className="pdf-modal-content">
              <button className="close-btn" onClick={onClose}>×</button>
              <p>
                {getText({
                  en: 'This document is not available for online reading.',
                  my: 'ဤစာရွက်စာတမ်းကို အွန်လိုင်းဖတ်ရှု၍ မရပါ။'
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`pdf-reader-modal ${isFullscreen ? 'fullscreen' : ''}`}>
      <div className="reader-overlay" onClick={!isFullscreen ? onClose : undefined}></div>
      
      <div className="reader-container" ref={modalRef}>
        {/* Header */}
        <header className="reader-header">
          <div className="header-left">
            <div className="resource-info">
              <h3 className="reader-title" title={getText(resource.title)}>
                {getText(resource.title)}
              </h3>
              <p className="reader-author">
                {getText({
                  en: 'By',
                  mm: 'စာရေးသူ'
                })} {getText(resource.author)}
              </p>
            </div>
          </div>

          <div className="header-center">
            {/* Page Navigation */}
            <div className="page-navigation">
              <button
                onClick={() => handlePageChange('prev')}
                disabled={currentPage <= 1}
                className="nav-btn"
                title={getText({ en: 'Previous Page', mm: 'ယခင်စာမျက်နှာ' })}
              >
                <ChevronLeftIcon className="nav-icon" />
              </button>
              
              <div className="page-info">
                <input
                  type="number"
                  value={currentPage}
                  onChange={(e) => setCurrentPage(Math.max(1, Math.min(totalPages, parseInt(e.target.value) || 1)))}
                  className="page-input"
                  min="1"
                  max={totalPages}
                />
                <span className="page-separator">/</span>
                <span className="total-pages">{totalPages}</span>
              </div>
              
              <button
                onClick={() => handlePageChange('next')}
                disabled={currentPage >= totalPages}
                className="nav-btn"
                title={getText({ en: 'Next Page', mm: 'နောက်စာမျက်နှာ' })}
              >
                <ChevronRightIcon className="nav-icon" />
              </button>
            </div>
          </div>

          <div className="header-right">
            {/* Toolbar */}
            <div className="reader-toolbar">
              {/* Zoom Controls */}
              <div className="zoom-controls">
                <button
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 50}
                  className="toolbar-btn"
                  title={getText({ en: 'Zoom Out', mm: 'ချုံ့ကြည့်ရန်' })}
                >
                  <MagnifyingGlassMinusIcon className="toolbar-icon" />
                </button>
                
                <span className="zoom-level">{zoomLevel}%</span>
                
                <button
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 200}
                  className="toolbar-btn"
                  title={getText({ en: 'Zoom In', mm: 'ချဲ့ကြည့်ရန်' })}
                >
                  <MagnifyingGlassPlusIcon className="toolbar-icon" />
                </button>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button
                  onClick={handleBookmark}
                  className="toolbar-btn"
                  title={getText({ en: 'Bookmark Page', mm: 'စာမျက်နှာ မှတ်သားရန်' })}
                >
                  <BookmarkIcon className="toolbar-icon" />
                </button>

                <button
                  onClick={handlePrint}
                  className="toolbar-btn"
                  title={getText({ en: 'Print', mm: 'ပုံနှိပ်ရန်' })}
                >
                  <PrinterIcon className="toolbar-icon" />
                </button>

                <button
                  onClick={handleShare}
                  className="toolbar-btn"
                  title={getText({ en: 'Share', mm: 'မျှဝေရန်' })}
                >
                  <ShareIcon className="toolbar-icon" />
                </button>

                <button
                  onClick={handleDownload}
                  className="toolbar-btn"
                  title={getText({ en: 'Download', mm: 'ဒေါင်းလုဒ်' })}
                >
                  <ArrowDownTrayIcon className="toolbar-icon" />
                </button>

                <button
                  onClick={toggleFullscreen}
                  className="toolbar-btn"
                  title={getText({ 
                    en: isFullscreen ? 'Exit Fullscreen' : 'Fullscreen',
                    mm: isFullscreen ? 'မျက်နှာပြင်အပြည့် ထွက်ရန်' : 'မျက်နှာပြင်အပြည့်'
                  })}
                >
                  {isFullscreen ? (
                    <ArrowsPointingInIcon className="toolbar-icon" />
                  ) : (
                    <ArrowsPointingOutIcon className="toolbar-icon" />
                  )}
                </button>

                <button
                  onClick={onClose}
                  className="toolbar-btn close-btn"
                  title={getText({ en: 'Close', mm: 'ပိတ်ရန်' })}
                >
                  <XMarkIcon className="toolbar-icon" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="reader-content">
          {isLoading && (
            <div className="reader-loading">
              <div className="loading-spinner large"></div>
              <p className="loading-text">
                {getText({
                  en: 'Loading PDF...',
                  mm: 'PDF ဖွင့်နေသည်...'
                })}
              </p>
            </div>
          )}

          {error && (
            <div className="reader-error">
              <div className="error-content">
                <h4 className="error-title">
                  {getText({
                    en: 'Unable to Load PDF',
                    mm: 'PDF ဖွင့်၍မရပါ'
                  })}
                </h4>
                <p className="error-message">{error}</p>
                <div className="error-actions">
                  <button onClick={handleDownload} className="btn-primary">
                    {getText({
                      en: 'Download Instead',
                      mm: 'ဒေါင်းလုဒ်လုပ်ရန်'
                    })}
                  </button>
                  <button onClick={onClose} className="btn-secondary">
                    {getText({
                      en: 'Close',
                      mm: 'ပိတ်ရန်'
                    })}
                  </button>
                </div>
              </div>
            </div>
          )}

          {!error && (
            <div className="pdf-container" style={{ transform: `scale(${zoomLevel / 100})` }}>
              <iframe
                ref={iframeRef}
                src={`${resource.readUrl}#page=1&zoom=100`}
                title={getText(resource.title)}
                width="100%"
                height="100%"
                className="pdf-viewer"
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                sandbox="allow-scripts allow-same-origin"
              />
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="reader-footer">
          <div className="footer-info">
            <span className="file-info">
              {resource.format} • {resource.fileSize}
            </span>
            <span className="publish-info">
              {getText({
                en: 'Published',
                mm: 'ထုတ်ဝေသည့်ရက်'
              })}: {new Date(resource.publishDate).toLocaleDateString(
                currentLanguage === 'en' ? 'en-US' : 'my-MM'
              )}
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default PDFReader;